import type { Difficulty, DifficultyConfig } from './types';

/**
 * Five graduated levels.
 *
 * The progression is intentionally smooth so a player who masters one level
 * can step up without hitting a wall:
 *
 *  1 Beginner  — depth 1, heavy noise. Plays the best move ~half the time;
 *                otherwise picks a serviceable alternative. Notices the
 *                immediate consequence of moves but no further.
 *  2 Casual    — depth 2, mild noise. Sees the opponent's reply but not the
 *                follow-up.
 *  3 Steady    — depth 4 (deterministic, no transposition cache).
 *                Sees two-move tactics reliably.
 *  4 Sharp     — depth 6, transposition cache. Iterative deepening within
 *                a 600 ms budget. Sees three-move tactics.
 *  5 Expert    — iterative deepening to depth 12, transposition cache,
 *                full 2 s budget. Will exhaust most amateurs.
 *
 *  All levels respect Kōnane's direction-lock and consider stopping a
 *  multi-capture early as a strategic option.
 */
export const DIFFICULTIES: readonly DifficultyConfig[] = [
  {
    level: 1,
    name: 'Beginner',
    description: 'Plays legal moves and notices one move ahead. Beatable with planning.',
    searchOpts: {
      maxDepth: 1,
      timeBudgetMs: 0,
      useTransposition: false,
      rootNoise: 0.55,
      considerStoppingEarly: false,
    },
  },
  {
    level: 2,
    name: 'Casual',
    description: 'Sees the opponent reply. Solid for a beginner who is trying hard.',
    searchOpts: {
      maxDepth: 2,
      timeBudgetMs: 0,
      useTransposition: false,
      rootNoise: 0.25,
      considerStoppingEarly: true,
    },
  },
  {
    level: 3,
    name: 'Steady',
    description: 'Two-move tactics. A reliable opponent.',
    searchOpts: {
      maxDepth: 4,
      timeBudgetMs: 0,
      useTransposition: false,
      rootNoise: 0.05,
      considerStoppingEarly: true,
    },
  },
  {
    level: 4,
    name: 'Sharp',
    description: 'Three-move tactics with a transposition cache. Challenging.',
    searchOpts: {
      maxDepth: 6,
      timeBudgetMs: 600,
      useTransposition: true,
      rootNoise: 0,
      considerStoppingEarly: true,
    },
  },
  {
    level: 5,
    name: 'Expert',
    description: 'Deep search up to two seconds. Plays its strongest game.',
    searchOpts: {
      maxDepth: 12,
      timeBudgetMs: 2000,
      useTransposition: true,
      rootNoise: 0,
      considerStoppingEarly: true,
    },
  },
] as const;

export function difficultyByLevel(level: Difficulty): DifficultyConfig {
  const found = DIFFICULTIES.find((d) => d.level === level);
  if (!found) throw new Error(`Unknown difficulty level: ${level}`);
  return found;
}
